class FacetFiltersForm extends HTMLElement {
  constructor() {
    super();
    this.onActiveFilterClick = this.onActiveFilterClick.bind(this);
    this.onClientPriceInput = this.onClientPriceInput.bind(this);
    this.onCollectionFacetEscape = this.onCollectionFacetEscape.bind(this);
    this.onFormSubmit = this.onFormSubmit.bind(this);

    this.debouncedOnSubmit = debounce((event) => {
      this.onSubmitHandler(event);
    }, 800);

    const facetForm = this.querySelector('form');
    facetForm.addEventListener('input', this.onClientPriceInput);
    facetForm.addEventListener('input', this.debouncedOnSubmit.bind(this));
    facetForm.addEventListener('submit', this.onFormSubmit);
    facetForm.addEventListener('keyup', this.onCollectionFacetEscape, true);

    const facetWrapper = this.querySelector('#FacetsWrapperDesktop');
    if (facetWrapper) facetWrapper.addEventListener('keyup', onKeyUpEscape);
  }

  static setListeners() {
    const onHistoryChange = (event) => {
      const searchParams = event.state ? event.state.searchParams : FacetFiltersForm.searchParamsInitial;
      if (searchParams === FacetFiltersForm.searchParamsPrev) return;
      FacetFiltersForm.renderPage(searchParams, null, false);
    };
    window.addEventListener('popstate', onHistoryChange);

    if (!FacetFiltersForm.outsideClickListener) {
      FacetFiltersForm.outsideClickListener = (event) => {
        document
          .querySelectorAll('.facets .facets__disclosure.disclosure-has-popup[open]')
          .forEach((details) => {
            if (!details.contains(event.target)) {
              FacetFiltersForm.closeDisclosure(details, { animated: true });
            }
          });
      };
      document.addEventListener('click', FacetFiltersForm.outsideClickListener);

      document.addEventListener(
        'click',
        (event) => {
          const summary = event.target.closest(
            '.gigi-collection-grid .facets .facets__disclosure.disclosure-has-popup > summary'
          );
          if (!summary) return;

          const details = summary.parentElement;

          if (details.hasAttribute('open')) {
            if (event.target.closest('.facets__display')) return;

            event.preventDefault();
            FacetFiltersForm.closeDisclosure(details, { animated: true });
            return;
          }

          document
            .querySelectorAll('.gigi-collection-grid .facets .facets__disclosure.disclosure-has-popup[open]')
            .forEach((openDetails) => {
              if (openDetails !== details) {
                FacetFiltersForm.closeDisclosure(openDetails, { animated: false });
              }
            });
        },
        true
      );

      document.addEventListener('click', (event) => {
        const option = event.target.closest('.gigi-collection-grid .facets__sort-option');
        if (!option) return;

        const details = option.closest('.facets__disclosure--sort');
        const form = option.closest('form');
        const input = form?.querySelector('input[name="sort_by"]');
        const label = details?.querySelector('[data-sort-label]');
        if (!details || !form || !input) return;

        event.preventDefault();
        input.value = option.dataset.value;
        const sortLabel = option.dataset.sortLabel || option.textContent.trim();
        if (label) label.textContent = sortLabel;
        details.querySelectorAll('.facets__sort-option').forEach((button) => {
          button.classList.toggle('is-active', button === option);
        });

        document
          .querySelectorAll('.gigi-collection-grid .facets__disclosure--sort[open]')
          .forEach((openDetails) => {
            if (openDetails !== details) {
              FacetFiltersForm.closeDisclosure(openDetails, { animated: false });
            }
          });

        FacetFiltersForm.closeDisclosure(details, { animated: true });
        if (!FacetFiltersForm.applyClientSort(input.value, sortLabel, form)) {
          form.dispatchEvent(new Event('input', { bubbles: true }));
        }
      });

      document.addEventListener('click', (event) => {
        const applyButton = event.target.closest('.gigi-collection-grid [data-gigi-price-apply]');
        const pricePill = event.target.closest('.gigi-collection-grid [data-gigi-price-pill]');
        const flavourPill = event.target.closest('.gigi-collection-grid [data-gigi-flavour-pill]');
        if (!applyButton && !pricePill && !flavourPill) return;

        event.preventDefault();

        if (applyButton) {
          FacetFiltersForm.showPriceApplyPress(applyButton);
          FacetFiltersForm.applyClientPriceFilter(applyButton);
          return;
        }

        if (pricePill) {
          FacetFiltersForm.resetClientPriceFilter(pricePill);
          return;
        }

        if (flavourPill) {
          FacetFiltersForm.resetClientFlavourFilter(flavourPill.dataset.gigiFlavourPill, flavourPill);
        }
      });

      window.addEventListener(
        'resize',
        debounce(() => FacetFiltersForm.syncProductCountTabsLayout(document), 120)
      );
    }
  }

  static closeDisclosure(details, { animated = false } = {}) {
    if (!details.hasAttribute('open')) return;

    const summary = details.querySelector('summary');
    const panel = details.querySelector('.facets__display');
    const shouldAnimate =
      animated &&
      panel &&
      panel.closest('.gigi-collection-grid') &&
      !window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    if (!shouldAnimate) {
      panel?.classList.remove('is-hiding');
      details.removeAttribute('open');
      if (summary) summary.setAttribute('aria-expanded', 'false');
      return;
    }

    if (panel.classList.contains('is-hiding')) return;

    panel.classList.add('is-hiding');

    const finish = () => {
      panel.classList.remove('is-hiding');
      if (!details.hasAttribute('open')) return;
      details.removeAttribute('open');
      if (summary) summary.setAttribute('aria-expanded', 'false');
    };

    const onTransitionEnd = (event) => {
      if (event.target !== panel) return;
      panel.removeEventListener('transitionend', onTransitionEnd);
      clearTimeout(fallback);
      finish();
    };

    panel.addEventListener('transitionend', onTransitionEnd);
    const fallback = window.setTimeout(() => {
      panel.removeEventListener('transitionend', onTransitionEnd);
      finish();
    }, 280);
  }

  static toggleActiveFacets(disable = true) {
    document.querySelectorAll('.js-facet-remove').forEach((element) => {
      element.classList.toggle('disabled', disable);
    });
  }

  static updateClearAllState() {
    const grid = document.querySelector('.gigi-collection-grid');
    if (!grid) return;

    const hasActivePills = grid.querySelectorAll('.active-facets__button').length > 0;
    const hasFlavour = grid.querySelectorAll('.gigi-flavour-filter__input:checked').length > 0;
    const priceRange = FacetFiltersForm.getCurrentClientPriceRange(grid);
    const hasPrice = priceRange.min !== null || priceRange.max !== null;
    const isActive = hasActivePills || hasFlavour || hasPrice;

    grid.querySelectorAll('.active-facets__button-remove.gigi-simple-button').forEach((link) => {
      link.classList.toggle('is-disabled', !isActive);
      link.setAttribute('aria-disabled', isActive ? 'false' : 'true');
      if (!isActive) {
        link.setAttribute('tabindex', '-1');
      } else {
        link.removeAttribute('tabindex');
      }
    });
  }

  static resetClientSideFilters() {
    document.querySelectorAll('.gigi-flavour-filter__input:checked').forEach((input) => {
      input.checked = false;
    });
    document.querySelectorAll('input[name="filter.v.price.gte"], input[name="filter.v.price.lte"]').forEach((input) => {
      input.value = '';
    });
    FacetFiltersForm.clientPriceRange = { min: null, max: null };
    FacetFiltersForm.syncClientPriceControls();

    if (window.gigiFlavourFilter?.resetAll) {
      window.gigiFlavourFilter.resetAll();
    }

    FacetFiltersForm.applyCurrentClientFilters();
  }

  static getClientSortRoot(element = document) {
    if (element?.closest) {
      return element.closest('.gigi-collection-grid[data-gigi-client-sort]');
    }

    return document.querySelector('.gigi-collection-grid[data-gigi-client-sort]');
  }

  static getCurrentClientSortValue(root = document) {
    return (
      FacetFiltersForm.clientSortValue ||
      root.querySelector?.('input[name="sort_by"], select[name="sort_by"]')?.value ||
      'price-ascending'
    );
  }

  static isPriceInput(element) {
    return element?.matches?.('input[name="filter.v.price.gte"], input[name="filter.v.price.lte"]');
  }

  static parsePriceInputValue(value) {
    const normalizedValue = String(value || '')
      .replace(/,/g, '')
      .replace(/\s/g, '')
      .trim();
    if (!normalizedValue) return null;

    const price = Number(normalizedValue);
    return Number.isFinite(price) ? Math.round(price * 100) : null;
  }

  static getEnteredClientPriceRange(root = document) {
    const getInputValue = (name) => {
      const input = Array.from(root.querySelectorAll(`input[name="${name}"]`)).find(
        (field) => field.value.trim() !== ''
      );
      return FacetFiltersForm.parsePriceInputValue(input?.value);
    };

    return {
      min: getInputValue('filter.v.price.gte'),
      max: getInputValue('filter.v.price.lte'),
    };
  }

  static getCurrentClientPriceRange() {
    return FacetFiltersForm.clientPriceRange || { min: null, max: null };
  }

  static syncClientPriceInputs(input) {
    const root = FacetFiltersForm.getClientSortRoot(input);
    if (!root || !FacetFiltersForm.isPriceInput(input)) return false;

    root.querySelectorAll(`input[name="${input.name}"]`).forEach((field) => {
      if (field !== input) field.value = input.value;
    });

    return true;
  }

  static syncClientPriceControls(root = document) {
    const priceRange = FacetFiltersForm.getCurrentClientPriceRange();
    const hasPriceFilter = priceRange.min !== null || priceRange.max !== null;
    const priceLabel = FacetFiltersForm.getClientPriceLabel(root, priceRange);

    root.querySelectorAll('[data-gigi-price-pill]').forEach((button) => {
      button.setAttribute(
        'aria-label',
        hasPriceFilter ? `Remove price filter ${priceLabel}` : 'Remove price filter'
      );
      const label = button.querySelector('[data-gigi-price-pill-label]');
      if (label) label.textContent = priceLabel;

      if (hasPriceFilter) {
        FacetFiltersForm.showFilterPill(button);
      } else {
        FacetFiltersForm.hideFilterPill(button);
      }
    });
  }

  static syncClientFlavourPills(root = document) {
    const pillLists = root.querySelectorAll('[data-gigi-flavour-pill-list]');
    if (!pillLists.length) return;

    const selectedFlavours = new Map();
    root.querySelectorAll('.gigi-flavour-filter__input:checked').forEach((input) => {
      if (!input.value || selectedFlavours.has(input.value)) return;
      selectedFlavours.set(input.value, input.dataset.flavourLabel || input.value);
    });

    pillLists.forEach((pillList) => {
      const remainingFlavours = new Map(selectedFlavours);

      pillList.querySelectorAll('[data-gigi-flavour-pill]').forEach((button) => {
        const value = button.dataset.gigiFlavourPill;
        if (!remainingFlavours.has(value)) {
          FacetFiltersForm.removeFilterPill(button);
          return;
        }

        const label = remainingFlavours.get(value);
        window.clearTimeout(Number(button.dataset.gigiFilterPillTimer || 0));
        button.classList.remove('is-exiting');
        button.setAttribute('aria-label', `Remove flavour filter ${label}`);
        button.querySelector('span').textContent = label;
        FacetFiltersForm.showFilterPill(button);
        remainingFlavours.delete(value);
      });

      remainingFlavours.forEach((label, value) => {
        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'gigi-filter-pill gigi-flavour-filter-pill hidden';
        button.dataset.gigiFlavourPill = value;
        button.setAttribute('aria-label', `Remove flavour filter ${label}`);
        button.innerHTML = `<span></span><span aria-hidden="true">x</span>`;
        button.querySelector('span').textContent = label;
        pillList.appendChild(button);
        FacetFiltersForm.showFilterPill(button);
      });
    });
  }

  static syncProductCountTabsLayout(root = document) {
    root.querySelectorAll('.product-count').forEach((productCount) => {
      const tabs = productCount.querySelector('[data-gigi-active-filter-tabs]');
      const countText = productCount.querySelector('.product-count__text');
      if (!tabs || !countText) return;

      const isVisible = (element) => {
        const rect = element.getBoundingClientRect();
        const style = window.getComputedStyle(element);
        return style.display !== 'none' && style.visibility !== 'hidden' && rect.width > 0 && rect.height > 0;
      };

      const hasVisibleTabs = Array.from(tabs.querySelectorAll('.gigi-filter-pill')).some(isVisible);
      productCount.classList.remove('gigi-product-count--stacked');
      if (!isVisible(productCount) || !hasVisibleTabs) return;

      const container = productCount.closest('.facets-container') || productCount.parentElement;
      const availableWidth = container?.clientWidth || productCount.clientWidth;
      const gap = parseFloat(window.getComputedStyle(productCount).columnGap || '0') || 0;
      const requiredWidth = tabs.scrollWidth + countText.scrollWidth + gap;

      productCount.classList.toggle('gigi-product-count--stacked', requiredWidth > availableWidth);
    });
  }

  static showFilterPill(pill) {
    if (!pill || !pill.classList.contains('hidden')) return;

    window.clearTimeout(Number(pill.dataset.gigiFilterPillTimer || 0));
    pill.classList.remove('hidden', 'is-exiting');
    pill.classList.add('is-entering');
    window.setTimeout(() => pill.classList.remove('is-entering'), 220);
  }

  static hideFilterPill(pill) {
    if (!pill || pill.classList.contains('hidden') || pill.classList.contains('is-exiting')) return;

    FacetFiltersForm.removeFilterPill(pill, { hideOnly: true });
  }

  static removeFilterPill(pill, { hideOnly = false } = {}) {
    if (!pill || pill.classList.contains('is-exiting')) return;

    pill.classList.remove('is-entering');
    pill.classList.add('is-exiting');
    const timer = window.setTimeout(() => {
      pill.classList.remove('is-exiting');
      if (hideOnly) {
        pill.classList.add('hidden');
        return;
      }

      pill.remove();
    }, 180);
    pill.dataset.gigiFilterPillTimer = String(timer);
  }

  static getClientPriceLabel(root = document, priceRange = FacetFiltersForm.getCurrentClientPriceRange()) {
    const currencySymbol = root.querySelector('.facets__price .field-currency')?.textContent.trim() || '';
    const formatPrice = (price) => {
      const value = price / 100;
      const formattedValue = new Intl.NumberFormat(undefined, {
        maximumFractionDigits: value % 1 === 0 ? 0 : 2,
      }).format(value);

      return `${currencySymbol}${formattedValue}`;
    };

    if (priceRange.min !== null && priceRange.max !== null) {
      if (priceRange.min === priceRange.max) return formatPrice(priceRange.min);
      return `${formatPrice(priceRange.min)} - ${formatPrice(priceRange.max)}`;
    }

    if (priceRange.min !== null) return `${formatPrice(priceRange.min)}+`;
    if (priceRange.max !== null) return `Under ${formatPrice(priceRange.max)}`;

    return '';
  }

  static showPriceApplyPress(button) {
    if (!button) return;

    button.classList.add('is-pressed');
    window.setTimeout(() => button.classList.remove('is-pressed'), 160);
  }

  static applyCurrentClientFilters(source = document) {
    const root = FacetFiltersForm.getClientSortRoot(source);
    const grid = root?.querySelector('#product-grid');
    if (!root || !grid) return false;

    const emptyState = root.querySelector('.gigi-collection-search-empty');
    const searchQuery = root.querySelector('.gigi-collection-toolbar__search-input')?.value.trim().toLowerCase() || '';
    const selectedFlavours = new Set(
      Array.from(root.querySelectorAll('.gigi-flavour-filter__input:checked')).map((input) => input.value)
    );
    const priceRange = FacetFiltersForm.getCurrentClientPriceRange();
    const hasPriceFilter = priceRange.min !== null || priceRange.max !== null;
    let visibleCount = 0;

    grid.querySelectorAll('.grid__item').forEach((item) => {
      const title = item.querySelector('.card__heading')?.textContent.trim().toLowerCase() || '';
      const price = Number(item.dataset.gigiSortPrice) || 0;
      const flavourIds = (item.dataset.gigiFlavourIds || '')
        .split(',')
        .map((id) => id.trim())
        .filter(Boolean);

      const matchesSearch = !searchQuery || title.includes(searchQuery);
      const matchesFlavour =
        selectedFlavours.size === 0 || flavourIds.some((id) => selectedFlavours.has(id));
      const matchesMin = priceRange.min === null || price >= priceRange.min;
      const matchesMax = priceRange.max === null || price <= priceRange.max;
      const matches = matchesSearch && matchesFlavour && matchesMin && matchesMax;

      item.hidden = !matches;
      if (matches) visibleCount += 1;
    });

    const hasClientFilter = searchQuery.length > 0 || selectedFlavours.size > 0 || hasPriceFilter;
    const showEmpty = hasClientFilter && visibleCount === 0;
    emptyState?.classList.toggle('hidden', !showEmpty);
    grid.classList.toggle('gigi-collection-search--empty', showEmpty);

    FacetFiltersForm.updateClearAllState();
    FacetFiltersForm.syncClientPriceControls(root);
    FacetFiltersForm.syncClientFlavourPills(root);
    FacetFiltersForm.syncProductCountTabsLayout(root);
    return true;
  }

  static syncClientPriceFilterFromInput(input) {
    return FacetFiltersForm.syncClientPriceInputs(input);
  }

  static applyClientPriceFilter(source = document) {
    const root = FacetFiltersForm.getClientSortRoot(source);
    if (!root) return false;

    FacetFiltersForm.clientPriceRange = FacetFiltersForm.getEnteredClientPriceRange(root);
    return FacetFiltersForm.applyCurrentClientFilters(root);
  }

  static resetClientPriceFilter(source = document) {
    const root = FacetFiltersForm.getClientSortRoot(source);
    if (!root) return false;

    root.querySelectorAll('input[name="filter.v.price.gte"], input[name="filter.v.price.lte"]').forEach((input) => {
      input.value = '';
    });
    FacetFiltersForm.clientPriceRange = { min: null, max: null };
    return FacetFiltersForm.applyCurrentClientFilters(root);
  }

  static resetClientFlavourFilter(flavourId, source = document) {
    const root = FacetFiltersForm.getClientSortRoot(source);
    if (!root || !flavourId) return false;

    root.querySelectorAll(`.gigi-flavour-filter__input[value="${CSS.escape(flavourId)}"]`).forEach((input) => {
      input.checked = false;
    });

    if (window.gigiFlavourFilter?.syncSelectedFromInputs) {
      window.gigiFlavourFilter.syncSelectedFromInputs();
    }

    return FacetFiltersForm.applyCurrentClientFilters(root);
  }

  static closeClientPriceDisclosure(source) {
    const details = source?.closest?.('details[open]');
    if (!details) return;

    const summary = details.querySelector('summary');
    summary?.classList.remove('focused');

    if (document.activeElement && details.contains(document.activeElement)) {
      document.activeElement.blur();
    }

    FacetFiltersForm.closeDisclosure(details, { animated: true });
  }

  static getSortConfig(sortValue) {
    if (sortValue === 'title-ascending') return { key: 'title', direction: 1 };
    if (sortValue === 'title-descending') return { key: 'title', direction: -1 };
    if (sortValue === 'price-ascending') return { key: 'price', direction: 1 };
    if (sortValue === 'price-descending') return { key: 'price', direction: -1 };
    if (sortValue === 'created-ascending') return { key: 'created', direction: 1 };
    if (sortValue === 'created-descending') return { key: 'created', direction: -1 };

    return null;
  }

  static syncSortControls(sortValue, sortLabel, root = document) {
    let activeLabel = sortLabel || FacetFiltersForm.clientSortLabel || '';

    root.querySelectorAll('input[name="sort_by"]').forEach((input) => {
      input.value = sortValue;
    });

    root.querySelectorAll('select[name="sort_by"]').forEach((select) => {
      select.value = sortValue;
      activeLabel = activeLabel || select.selectedOptions?.[0]?.textContent.trim() || '';
    });

    root.querySelectorAll('.facets__sort-option, .gigi-mobile-facets__sort-option').forEach((button) => {
      const isActive = button.dataset.value === sortValue;
      button.classList.toggle('is-active', isActive);
      if (isActive) {
        activeLabel = button.dataset.sortLabel || button.textContent.trim() || activeLabel;
      }
    });

    if (!activeLabel) activeLabel = sortValue;
    FacetFiltersForm.clientSortLabel = activeLabel;

    root.querySelectorAll('.facets__summary-label[data-sort-label], [data-sort-active-label]').forEach((label) => {
      label.textContent = activeLabel;
    });
  }

  static sortProductsInPlace(sortValue, root = document) {
    const sortRoot = FacetFiltersForm.getClientSortRoot(root);
    const sortConfig = FacetFiltersForm.getSortConfig(sortValue);
    const grid = sortRoot?.querySelector('#product-grid');
    if (!sortRoot || !sortConfig || !grid) return false;

    const items = Array.from(grid.querySelectorAll(':scope > .grid__item'));
    if (items.length === 0) return false;

    const sortedItems = items
      .map((item, index) => ({ item, index }))
      .sort((first, second) => {
        let comparison = 0;

        if (sortConfig.key === 'title') {
          comparison = (first.item.dataset.gigiSortTitle || '').localeCompare(
            second.item.dataset.gigiSortTitle || '',
            undefined,
            { sensitivity: 'base' }
          );
        } else {
          const dataKey = `gigiSort${sortConfig.key[0].toUpperCase()}${sortConfig.key.slice(1)}`;
          const firstValue = Number(first.item.dataset[dataKey]) || 0;
          const secondValue = Number(second.item.dataset[dataKey]) || 0;
          comparison = firstValue - secondValue;
        }

        return comparison === 0 ? first.index - second.index : comparison * sortConfig.direction;
      });

    // The grid is server-rendered in the default sort order; re-appending
    // unchanged items would restart their entrance animations.
    const orderUnchanged = sortedItems.every(({ item }, index) => item === items[index]);
    if (orderUnchanged) return true;

    const fragment = document.createDocumentFragment();
    sortedItems.forEach(({ item }, index) => {
      item.style.setProperty('--animation-order', index + 1);
      fragment.appendChild(item);
    });
    grid.appendChild(fragment);

    return true;
  }

  static applyClientSort(sortValue, sortLabel, source = document) {
    const root = FacetFiltersForm.getClientSortRoot(source);
    if (!root) return false;

    FacetFiltersForm.clientSortValue = sortValue;
    FacetFiltersForm.syncSortControls(sortValue, sortLabel, root);
    const didSort = FacetFiltersForm.sortProductsInPlace(sortValue, root);
    FacetFiltersForm.applyCurrentClientFilters(root);
    return didSort;
  }

  static applyCurrentClientSort() {
    const root = FacetFiltersForm.getClientSortRoot();
    if (!root) return;

    const sortValue = FacetFiltersForm.getCurrentClientSortValue(root);
    FacetFiltersForm.clientSortValue = sortValue;
    FacetFiltersForm.syncSortControls(sortValue, FacetFiltersForm.clientSortLabel, root);
    FacetFiltersForm.sortProductsInPlace(sortValue, root);
    FacetFiltersForm.applyCurrentClientFilters(root);
  }

  static renderPage(searchParams, event, updateURLHash = true) {
    FacetFiltersForm.searchParamsPrev = searchParams;
    const sections = FacetFiltersForm.getSections();
    const countContainer = document.getElementById('ProductCount');
    const countContainerDesktop = document.getElementById('ProductCountDesktop');
    const loadingSpinners = document.querySelectorAll(
      '.facets-container .loading__spinner, facet-filters-form .loading__spinner'
    );
    loadingSpinners.forEach((spinner) => spinner.classList.remove('hidden'));
    document.getElementById('ProductGridContainer').querySelector('.collection').classList.add('loading');
    if (countContainer) {
      countContainer.classList.add('loading');
    }
    if (countContainerDesktop) {
      countContainerDesktop.classList.add('loading');
    }

    sections.forEach((section) => {
      const url = `${window.location.pathname}?section_id=${section.section}&${searchParams}`;
      const filterDataUrl = (element) => element.url === url;

      FacetFiltersForm.filterData.some(filterDataUrl)
        ? FacetFiltersForm.renderSectionFromCache(filterDataUrl, event)
        : FacetFiltersForm.renderSectionFromFetch(url, event);
    });

    if (updateURLHash && !FacetFiltersForm.getClientSortRoot()) FacetFiltersForm.updateURLHash(searchParams);
  }

  static renderSectionFromFetch(url, event) {
    fetch(url)
      .then((response) => response.text())
      .then((responseText) => {
        const html = responseText;
        FacetFiltersForm.filterData = [...FacetFiltersForm.filterData, { html, url }];
        FacetFiltersForm.renderFilters(html, event);
        FacetFiltersForm.renderProductGridContainer(html);
        FacetFiltersForm.renderProductCount(html);
        FacetFiltersForm.applyCurrentClientSort();
        if (typeof initializeScrollAnimationTrigger === 'function') initializeScrollAnimationTrigger(html.innerHTML);
      });
  }

  static renderSectionFromCache(filterDataUrl, event) {
    const html = FacetFiltersForm.filterData.find(filterDataUrl).html;
    FacetFiltersForm.renderFilters(html, event);
    FacetFiltersForm.renderProductGridContainer(html);
    FacetFiltersForm.renderProductCount(html);
    FacetFiltersForm.applyCurrentClientSort();
    if (typeof initializeScrollAnimationTrigger === 'function') initializeScrollAnimationTrigger(html.innerHTML);
  }

  static renderProductGridContainer(html) {
    document.getElementById('ProductGridContainer').innerHTML = new DOMParser()
      .parseFromString(html, 'text/html')
      .getElementById('ProductGridContainer').innerHTML;

    document
      .getElementById('ProductGridContainer')
      .querySelectorAll('.scroll-trigger')
      .forEach((element) => {
        element.classList.add('scroll-trigger--cancel');
      });
  }

  static renderProductCount(html) {
    const count = new DOMParser().parseFromString(html, 'text/html').getElementById('ProductCount').innerHTML;
    const container = document.getElementById('ProductCount');
    const containerDesktop = document.getElementById('ProductCountDesktop');
    container.innerHTML = count;
    container.classList.remove('loading');
    if (containerDesktop) {
      containerDesktop.innerHTML = count;
      containerDesktop.classList.remove('loading');
    }
    const loadingSpinners = document.querySelectorAll(
      '.facets-container .loading__spinner, facet-filters-form .loading__spinner'
    );
    loadingSpinners.forEach((spinner) => spinner.classList.add('hidden'));
  }

  static renderFilters(html, event) {
    const parsedHTML = new DOMParser().parseFromString(html, 'text/html');
    const facetDetailsElementsFromFetch = parsedHTML.querySelectorAll(
      '#FacetFiltersForm .js-filter, #FacetFiltersFormMobile .js-filter, #FacetFiltersPillsForm .js-filter'
    );
    const facetDetailsElementsFromDom = document.querySelectorAll(
      '#FacetFiltersForm .js-filter, #FacetFiltersFormMobile .js-filter, #FacetFiltersPillsForm .js-filter'
    );

    // Remove facets that are no longer returned from the server
    Array.from(facetDetailsElementsFromDom).forEach((currentElement) => {
      if (!Array.from(facetDetailsElementsFromFetch).some(({ id }) => currentElement.id === id)) {
        currentElement.remove();
      }
    });

    const matchesId = (element) => {
      const jsFilter = event ? event.target.closest('.js-filter') : undefined;
      return jsFilter ? element.id === jsFilter.id : false;
    };

    const facetsToRender = Array.from(facetDetailsElementsFromFetch).filter((element) => !matchesId(element));
    const countsToRender = Array.from(facetDetailsElementsFromFetch).find(matchesId);

    facetsToRender.forEach((elementToRender, index) => {
      const currentElement = document.getElementById(elementToRender.id);
      // Element already rendered in the DOM so just update the innerHTML
      if (currentElement) {
        document.getElementById(elementToRender.id).innerHTML = elementToRender.innerHTML;
      } else {
        if (index > 0) {
          const { className: previousElementClassName, id: previousElementId } = facetsToRender[index - 1];
          // Same facet type (eg horizontal/vertical or drawer/mobile)
          if (elementToRender.className === previousElementClassName) {
            document.getElementById(previousElementId).after(elementToRender);
            return;
          }
        }

        if (elementToRender.parentElement) {
          document.querySelector(`#${elementToRender.parentElement.id} .js-filter`).before(elementToRender);
        }
      }
    });

    FacetFiltersForm.renderActiveFacets(parsedHTML);
    FacetFiltersForm.renderAdditionalElements(parsedHTML);
    FacetFiltersForm.updateClearAllState();

    if (countsToRender) {
      const closestJSFilterID = event.target.closest('.js-filter').id;

      if (closestJSFilterID) {
        FacetFiltersForm.renderCounts(countsToRender, event.target.closest('.js-filter'));
        FacetFiltersForm.renderMobileCounts(countsToRender, document.getElementById(closestJSFilterID));

        const newFacetDetailsElement = document.getElementById(closestJSFilterID);
        const newElementSelector = newFacetDetailsElement.classList.contains('mobile-facets__details')
          ? `.mobile-facets__close-button`
          : `.facets__summary`;
        const newElementToActivate = newFacetDetailsElement.querySelector(newElementSelector);

        const isTextInput = event.target.getAttribute('type') === 'text';

        if (newElementToActivate && !isTextInput) newElementToActivate.focus();
      }
    }
  }

  static renderActiveFacets(html) {
    const activeFacetElementSelectors = ['.active-facets-mobile', '.active-facets-desktop'];

    activeFacetElementSelectors.forEach((selector) => {
      const activeFacetsElement = html.querySelector(selector);
      if (!activeFacetsElement) return;
      document.querySelector(selector).innerHTML = activeFacetsElement.innerHTML;
    });

    FacetFiltersForm.toggleActiveFacets(false);
    FacetFiltersForm.updateClearAllState();
  }

  static renderAdditionalElements(html) {
    const mobileElementSelectors = ['.gigi-mobile-facets__trigger', '.mobile-facets__open', '.mobile-facets__count', '.sorting'];

    mobileElementSelectors.forEach((selector) => {
      if (!html.querySelector(selector)) return;
      document.querySelector(selector).innerHTML = html.querySelector(selector).innerHTML;
    });

    document.getElementById('FacetFiltersFormMobile')?.closest('menu-drawer')?.bindEvents?.();
  }

  static renderCounts(source, target) {
    const targetSummary = target.querySelector('.facets__summary');
    const sourceSummary = source.querySelector('.facets__summary');

    if (sourceSummary && targetSummary) {
      targetSummary.outerHTML = sourceSummary.outerHTML;
    }

    const targetHeaderElement = target.querySelector('.facets__header');
    const sourceHeaderElement = source.querySelector('.facets__header');

    if (sourceHeaderElement && targetHeaderElement) {
      targetHeaderElement.outerHTML = sourceHeaderElement.outerHTML;
    }

    const targetWrapElement = target.querySelector('.facets-wrap');
    const sourceWrapElement = source.querySelector('.facets-wrap');

    if (sourceWrapElement && targetWrapElement) {
      const isShowingMore = Boolean(target.querySelector('show-more-button .label-show-more.hidden'));
      if (isShowingMore) {
        sourceWrapElement
          .querySelectorAll('.facets__item.hidden')
          .forEach((hiddenItem) => hiddenItem.classList.replace('hidden', 'show-more-item'));
      }

      targetWrapElement.outerHTML = sourceWrapElement.outerHTML;
    }
  }

  static renderMobileCounts(source, target) {
    const targetFacetsList = target.querySelector('.mobile-facets__list');
    const sourceFacetsList = source.querySelector('.mobile-facets__list');

    if (sourceFacetsList && targetFacetsList) {
      targetFacetsList.outerHTML = sourceFacetsList.outerHTML;
    }
  }

  static updateURLHash(searchParams) {
    history.pushState({ searchParams }, '', `${window.location.pathname}${searchParams && '?'.concat(searchParams)}`);
  }

  static getSections() {
    return [
      {
        section: document.getElementById('product-grid').dataset.id,
      },
    ];
  }

  createSearchParams(form) {
    const formData = new FormData(form);
    if (FacetFiltersForm.getClientSortRoot(form)) {
      formData.delete('sort_by');
      formData.delete('filter.v.price.gte');
      formData.delete('filter.v.price.lte');
    }
    return new URLSearchParams(formData).toString();
  }

  onSubmitForm(searchParams, event) {
    FacetFiltersForm.renderPage(searchParams, event);
  }

  onSubmitHandler(event) {
    if (FacetFiltersForm.syncClientPriceFilterFromInput(event.target)) {
      event.preventDefault();
      return;
    }

    if (
      event.target?.matches?.('[name="sort_by"]') &&
      FacetFiltersForm.applyClientSort(
        event.target.value,
        event.target.selectedOptions?.[0]?.textContent.trim(),
        event.target
      )
    ) {
      event.preventDefault();
      return;
    }

    if (event.target.closest('.gigi-flavour-filter')) {
      return;
    }
    event.preventDefault();
    const sortFilterForms = document.querySelectorAll('facet-filters-form form');
    if (event.srcElement.className == 'mobile-facets__checkbox') {
      const searchParams = this.createSearchParams(event.target.closest('form'));
      this.onSubmitForm(searchParams, event);
    } else {
      const forms = [];
      const isMobile = event.target.closest('form').id === 'FacetFiltersFormMobile';

      sortFilterForms.forEach((form) => {
        if (!isMobile) {
          if (form.id === 'FacetSortForm' || form.id === 'FacetFiltersForm' || form.id === 'FacetSortDrawerForm') {
            forms.push(this.createSearchParams(form));
          }
        } else if (form.id === 'FacetFiltersFormMobile') {
          forms.push(this.createSearchParams(form));
        }
      });
      this.onSubmitForm(forms.join('&'), event);
    }
  }

  onClientPriceInput(event) {
    FacetFiltersForm.syncClientPriceFilterFromInput(event.target);
  }

  onFormSubmit(event) {
    if (!FacetFiltersForm.getClientSortRoot(event.target)) {
      this.onSubmitHandler(event);
      return;
    }

    event.preventDefault();
    FacetFiltersForm.applyClientPriceFilter(event.target);
  }

  onCollectionFacetEscape(event) {
    if (event.code.toUpperCase() !== 'ESCAPE') return;

    const openDetailsElement = event.target.closest('.gigi-collection-grid details[open]');
    const summaryElement = openDetailsElement?.querySelector('summary');
    if (!openDetailsElement || !summaryElement) return;

    event.stopPropagation();
    openDetailsElement.removeAttribute('open');
    summaryElement.setAttribute('aria-expanded', 'false');
    summaryElement.classList.remove('focused');

    if (document.activeElement && openDetailsElement.contains(document.activeElement)) {
      document.activeElement.blur();
    }
  }

  onActiveFilterClick(event) {
    if (
      event.currentTarget.classList.contains('is-disabled') ||
      event.currentTarget.getAttribute('aria-disabled') === 'true'
    ) {
      event.preventDefault();
      return;
    }

    event.preventDefault();
    if (event.currentTarget.classList.contains('active-facets__button-remove')) {
      FacetFiltersForm.resetClientSideFilters();
    }
    FacetFiltersForm.toggleActiveFacets();
    const url =
      event.currentTarget.href.indexOf('?') == -1
        ? ''
        : event.currentTarget.href.slice(event.currentTarget.href.indexOf('?') + 1);
    FacetFiltersForm.renderPage(url);
  }
}

FacetFiltersForm.filterData = [];
FacetFiltersForm.clientPriceRange = { min: null, max: null };
FacetFiltersForm.clientSortValue = '';
FacetFiltersForm.clientSortLabel = '';
FacetFiltersForm.searchParamsInitial = window.location.search.slice(1);
FacetFiltersForm.searchParamsPrev = window.location.search.slice(1);
customElements.define('facet-filters-form', FacetFiltersForm);
FacetFiltersForm.setListeners();
document.addEventListener('DOMContentLoaded', () => FacetFiltersForm.applyCurrentClientSort());
document.addEventListener('shopify:section:load', () => FacetFiltersForm.applyCurrentClientSort());

class PriceRange extends HTMLElement {
  constructor() {
    super();
    this.querySelectorAll('input').forEach((element) => {
      element.addEventListener('change', this.onRangeChange.bind(this));
      element.addEventListener('keydown', this.onKeyDown.bind(this));
    });
    this.setMinAndMaxValues();
  }

  onRangeChange(event) {
    this.adjustToValidValues(event.currentTarget);
    this.setMinAndMaxValues();
    FacetFiltersForm.syncClientPriceFilterFromInput(event.currentTarget);
  }

  onKeyDown(event) {
    if (event.metaKey) return;

    if (event.key === 'Enter') {
      event.preventDefault();
      FacetFiltersForm.syncClientPriceFilterFromInput(event.currentTarget);
      const applyButton = this.querySelector('[data-gigi-price-apply]');
      FacetFiltersForm.showPriceApplyPress(applyButton);
      applyButton?.click();
      event.currentTarget.blur();
      FacetFiltersForm.closeClientPriceDisclosure(event.currentTarget);
      return;
    }

    const pattern = /[0-9]|\.|,|'| |Tab|Backspace|Enter|ArrowUp|ArrowDown|ArrowLeft|ArrowRight|Delete|Escape/;
    if (!event.key.match(pattern)) event.preventDefault();
  }

  setMinAndMaxValues() {
    const inputs = this.querySelectorAll('input');
    const minInput = inputs[0];
    const maxInput = inputs[1];
    if (maxInput.value) minInput.setAttribute('data-max', maxInput.value);
    if (minInput.value) maxInput.setAttribute('data-min', minInput.value);
    if (minInput.value === '') maxInput.setAttribute('data-min', 0);
    if (maxInput.value === '') minInput.setAttribute('data-max', maxInput.getAttribute('data-max'));
  }

  adjustToValidValues(input) {
    const value = Number(input.value);
    const min = Number(input.getAttribute('data-min'));
    const max = Number(input.getAttribute('data-max'));

    if (value < min) input.value = min;
    if (value > max) input.value = max;
  }
}

customElements.define('price-range', PriceRange);

class FacetRemove extends HTMLElement {
  constructor() {
    super();
    const facetLink = this.querySelector('a');
    facetLink.setAttribute('role', 'button');
    facetLink.addEventListener('click', this.closeFilter.bind(this));
    facetLink.addEventListener('keyup', (event) => {
      event.preventDefault();
      if (event.code.toUpperCase() === 'SPACE') this.closeFilter(event);
    });
  }

  closeFilter(event) {
    const facetLink = this.querySelector('a');
    if (
      facetLink?.classList.contains('is-disabled') ||
      facetLink?.getAttribute('aria-disabled') === 'true'
    ) {
      event.preventDefault();
      return;
    }

    event.preventDefault();
    const form = this.closest('facet-filters-form') || document.querySelector('facet-filters-form');
    form.onActiveFilterClick(event);
  }
}

customElements.define('facet-remove', FacetRemove);
